import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Ticket, ArrowUpRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { getEvents } from "../lib/api";

function fmtDate(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function Events() {
  const [events, setEvents] = useState([]);
  useEffect(() => { getEvents().then(setEvents).catch(() => {}); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.date >= today);
  const past = events.filter((e) => e.date < today);

  const Card = ({ e }) => (
    <StaggerItem>
      <Link to={`/events/${e.slug}`} data-testid={`event-card-${e.slug}`} className="group block rounded-[1.5rem] overflow-hidden bg-white border border-black/[0.07] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1.5 h-full">
        <div className="overflow-hidden aspect-[16/10] relative bg-nead-mist">
          {e.image && <img src={e.image} alt={e.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute top-4 left-4 rounded-xl bg-white/95 px-3 py-2 text-center shadow-sm">
            <div className="font-display font-bold text-lg leading-none text-nead-pink">{new Date(e.date + "T00:00:00").getDate()}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-nead-navy/60">{new Date(e.date + "T00:00:00").toLocaleDateString("en-GB", { month: "short" })}</div>
          </div>
          {e.ticket_type !== "none" && <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-nead-navy/90 px-3 py-1 text-xs font-semibold text-white"><Ticket className="h-3 w-3" />Tickets</span>}
        </div>
        <div className="p-6">
          <h3 className="font-display font-bold text-xl text-nead-navy flex items-start gap-2">{e.title}<ArrowUpRight className="h-4 w-4 mt-1 text-nead-navy/30 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></h3>
          <div className="mt-3 space-y-1.5 text-sm text-nead-navy/60">
            <div className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-nead-navy/40" />{fmtDate(e.date)}{e.time ? `, ${e.time}` : ""}</div>
            {e.venue && <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-nead-navy/40" />{e.venue}</div>}
          </div>
        </div>
      </Link>
    </StaggerItem>
  );

  return (
    <div>
      <PageHeader label="What's on" titleLines={["Festivals, gigs", "and events."]}
        intro="From landmark festivals to intimate gigs, here's what's coming up across Cumbria. Grab your tickets and plan your visit." />

      <section className="container-x py-24 md:py-32">
        <Reveal><h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy">Upcoming</h2></Reveal>
        {upcoming.length ? (
          <Stagger className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">{upcoming.map((e) => <Card key={e.slug} e={e} />)}</Stagger>
        ) : (
          <p className="mt-8 text-nead-navy/50">No upcoming events right now. Check back soon.</p>
        )}

        {past.length > 0 && (
          <>
            <Reveal><h2 className="mt-24 font-display font-bold text-3xl sm:text-4xl tracking-tight text-nead-navy">Past events</h2></Reveal>
            <Stagger className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-80">{past.map((e) => <Card key={e.slug} e={e} />)}</Stagger>
          </>
        )}
      </section>
    </div>
  );
}
